/* Kolor marki, from `profile-web-mockups-v2/f-zakladki.html` (`[data-view="marka"]`) and the
 * `.swatches` / `.picker` blocks it borrows from `_blocks.css`.
 *
 * ── The concentric radii are the section's own rule ─────────────────────────────────────────
 * The mockup states it in a comment: „inner radius = outer radius − inset". A 48px tile at radius
 * 16 holds a 32px swatch inset 8px, so the swatch takes 8. Both numbers are tokens rather than a
 * `calc`, because two stylesheets recomputing one derivation is how they come to disagree.
 *
 * ── The picker is HALF the row ──────────────────────────────────────────────────────────────
 * `minmax(0, 1fr) minmax(0, 1fr)`, the mockup's own override of the shared block's `1fr 200px`.
 * Both tracks need `minmax(0, …)`: a grid item's default `min-width: auto` refuses to shrink below
 * its content, and the HEX field inside the right-hand one is wider than half of a 1280px sheet.
 *
 * Every colour, space, radius and type value is a token; heights and widths are layout. */

/* The retry wears the screen's secondary pill (`AccountScreen.module.css`). Only the room above it is this
 * block's own. `shared/ui/stateAction.module.css` is the app's other retry box and is deliberately
 * NOT worn here: it paints `--bg-card` because every one of its consumers stands on a card, and
 * this one stands on the sheet's `--bg` canvas. */
.retry {
  margin-top: var(--space-3);
}

/* The quiet note beside „Własny kolor" — the mockup's `.blk>h3 small`, pushed to the far end. */
.blockNote {
  margin-left: auto;
  font-size: var(--fs-micro);
  font-weight: var(--fw-regular);
  color: var(--text-2);
}

/* ── The six ready-made colours ───────────────────────────────────────────────────────────── */

.tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-2);
}

.tile {
  display: flex;
  gap: var(--space-2-5px);
  align-items: center;
  height: var(--sz-account-tile);
  padding: var(--space-0) var(--space-3) var(--space-0) var(--space-2);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  text-align: left;
  cursor: pointer;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-account-tile);
}

/* The chosen tile's tick, at its far end — the choice in a mark as well as in the ring. */
.tileCheck {
  display: block;
  flex: none;
  width: var(--sz-account-glyph-md);
  height: var(--sz-account-glyph-md);
  margin-left: auto;
  color: var(--text);
}

.tile:hover {
  color: var(--text);
  border-color: var(--border-2);
}

.tile:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The chosen one. An INK ring rather than an accent one: the tile already carries a colour, and a
 * second colour marking it would be two hues arguing about which is the state. `--ring-field` is
 * the hairline every focused field in this app already wears — the shadow doubles the border so the
 * ring reads at two pixels without the box growing by one. */
.tileOn {
  color: var(--text);
  background: var(--bg-card);
  border-color: var(--text);
  box-shadow: var(--ring-field);
}

/* The swatch. Its fill is DATA — the preset's gradient, handed over inline by the component — so
 * the only thing declared here is the box and the hairline that keeps a pale colour off the tile. */
.swatch {
  flex: none;
  width: var(--sz-account-swatch);
  height: var(--sz-account-swatch);
  border: var(--border-hairline) solid var(--swatch-edge);
  border-radius: var(--radius-account-swatch);
}

/* ── The custom colour ─────────────────────────────────────────────────────────────────────── */

.custom {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-5);
}

/* The column beside the picker: the field, the measurement, the sentence and the two samples. */
.side {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  align-items: flex-start;
  min-width: 0;
}

.hexField {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  width: var(--w-account-hex);
  max-width: 100%;
  height: var(--h-account-button);
  padding: var(--space-0) var(--space-3);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-10-5px);
}

/* The current colour, beside the „#". Its fill is DATA, handed in as `--sample-fill`. */
.hexSwatch {
  flex: none;
  width: var(--sz-account-hex-swatch);
  height: var(--sz-account-hex-swatch);
  background: var(--sample-fill);
  border: var(--border-hairline) solid var(--swatch-edge);
  border-radius: var(--radius-4px);
}

.hexField:focus-within {
  border-color: var(--text);
}

.hexHash {
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.hexInput {
  flex: 1;
  min-width: 0;
  font-family: var(--font-body);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-account-hex);
  color: var(--text);
  text-transform: uppercase;
  font-variant-numeric: tabular-nums;
  background: transparent;
  border: 0;
  outline: none;
}

/* The AA measurement as a plain line with a tick — no chip. */
.aa {
  display: inline-flex;
  gap: var(--space-1-5px);
  align-items: center;
  font-size: var(--fs-note);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

/* What the picker cannot show on its own: the colour actually painted, or why nothing saves. */
.adjusted {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  color: var(--text-2);
}

.aaIcon {
  display: block;
  flex: none;
  width: var(--sz-account-glyph-sm);
  height: var(--sz-account-glyph-sm);
}

/* The two sample buttons, side by side, each with its caption under it. */
.samples {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-3);
  align-self: stretch;
}

.sample {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

/* A client's button, flat: the fill and the ink are DATA, handed in as custom properties. */
.sampleButton {
  display: grid;
  place-items: center;
  height: var(--h-account-button);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--sample-ink);
  background: var(--sample-fill);
  border-radius: var(--r-pill);
}

.sampleCaption {
  display: flex;
  gap: var(--space-2);
  justify-content: space-between;
  font-size: var(--fs-micro);
  color: var(--text-2);
}

.sampleHex {
  font-weight: var(--fw-regular);
  font-variant-numeric: tabular-nums;
}

/* ONE footer for the tab: a hairline, and „Zapisz" flush right. */
.footer {
  display: flex;
  justify-content: flex-end;
  padding-top: var(--space-4);
  border-top: var(--border-hairline) solid var(--border);
}
